import React, { useState, useEffect, useCallback } from 'react';
import { getOfferings, purchasePackage, restorePurchases } from '../services/revenuecat';
import { PREMIUM_FEATURES } from '../config/freemiumConfig';
import { isNative } from '../config/apiConfig';
import './PaywallView.css';

const FEATURE_ICONS = ['query_stats', 'notifications_active', 'newspaper', 'psychology'];

export default function PaywallView({ onSuccess, onClose }) {
  const [offerings, setOfferings]     = useState(null);
  const [selected, setSelected]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [restoring, setRestoring]     = useState(false);
  const [error, setError]             = useState('');
  const [offeringsError, setOfferingsError] = useState(false);

  const loadOfferings = useCallback(() => {
    if (!isNative()) return;
    setOfferingsError(false);
    const timeout = setTimeout(() => setOfferingsError(true), 10000);
    getOfferings().then(offering => {
      clearTimeout(timeout);
      if (!offering) { setOfferingsError(true); return; }
      setOfferings(offering);
      setOfferingsError(false);
      const yearly = offering.availablePackages.find(p => p.packageType === 'ANNUAL')
                  || offering.availablePackages[0];
      if (yearly) setSelected(yearly);
    }).catch(() => { clearTimeout(timeout); setOfferingsError(true); });
  }, []);

  useEffect(() => { loadOfferings(); }, [loadOfferings]);

  const handlePurchase = async () => {
    if (!selected) return;
    setLoading(true);
    setError('');
    try {
      const success = await purchasePackage(selected);
      if (success) onSuccess();
      else setError('Kauf nicht abgeschlossen.');
    } catch (err) {
      if (!err.message?.includes('cancelled')) {
        setError('Kauf fehlgeschlagen. Bitte versuche es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setError('');
    try {
      const success = await restorePurchases();
      if (success) onSuccess();
      else setError('Kein aktives Abo gefunden.');
    } catch {
      setError('Wiederherstellung fehlgeschlagen.');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="pw-overlay">
      <div className="pw-shell">
        {/* Header */}
        <header className="pw-header">
          <span className="pw-header-brand">BULLIVO</span>
          <span className="pw-header-label">BULLIVO PRO</span>
          <button className="pw-close" onClick={onClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>

        {/* Main Content */}
        <main className="pw-main">
          {/* Features */}
          <div className="pw-features">
            {PREMIUM_FEATURES.map((f, i) => (
              <div className="pw-feature" key={i}>
                <div className="pw-feature-icon-wrap">
                  <span className="material-symbols-rounded" style={
                    i === 1 || i === 3 ? { fontVariationSettings: "'FILL' 1" } : {}
                  }>{FEATURE_ICONS[i] || 'star'}</span>
                </div>
                <div className="pw-feature-text">
                  <span className="pw-feature-title">{f.title}</span>
                  <span className="pw-feature-desc">{f.desc}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Section */}
          <div className="pw-trust">
            <div className="pw-trust-stars">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="material-symbols-rounded pw-star-filled">star</span>
              ))}
              <span className="pw-trust-label">TRUSTED ANALYST TOOLS</span>
            </div>
            <p className="pw-trust-quote">
              "Die Signale haben meine Trading-Strategie grundlegend verbessert."
            </p>
          </div>

          {/* Packages */}
          {offerings && (
            <div className="pw-packages">
              {offerings.availablePackages
                .sort((a, b) => (a.packageType === 'ANNUAL' ? -1 : 1))
                .map(pkg => {
                const isYearly  = pkg.packageType === 'ANNUAL';
                const isMonthly = pkg.packageType === 'MONTHLY';
                const price     = pkg.product.price;
                const priceStr  = pkg.product.priceString;
                const monthly   = offerings.availablePackages.find(p => p.packageType === 'MONTHLY');
                const monthlyPrice = monthly ? monthly.product.price : 2.99;
                const perMonthYearly = isYearly ? (price / 12) : price;
                const savings   = isYearly && monthlyPrice > 0
                  ? Math.round((1 - (price / 12) / monthlyPrice) * 100)
                  : 0;
                const isActive  = selected?.identifier === pkg.identifier;
                return (
                  <button
                    key={pkg.identifier}
                    className={`pw-plan${isActive ? ' selected' : ''}${isYearly ? ' yearly' : ''}`}
                    onClick={() => setSelected(pkg)}
                  >
                    {isYearly && savings > 0 && (
                      <span className="pw-plan-badge">Spare {savings}%</span>
                    )}
                    <div className="pw-plan-header">
                      <span className="pw-plan-name">{isYearly ? 'Jährlich' : 'Monatlich'}</span>
                      <span className="pw-plan-radio">{isActive ? '●' : '○'}</span>
                    </div>
                    <div className="pw-plan-price">
                      <span className="pw-plan-amount">{priceStr}</span>
                      <span className="pw-plan-period">/ {isYearly ? 'Jahr' : 'Monat'}</span>
                    </div>
                    {isYearly && (
                      <span className="pw-plan-monthly">{perMonthYearly.toFixed(2).replace('.', ',')} € / Monat</span>
                    )}
                    {isMonthly && (
                      <span className="pw-plan-monthly">Jederzeit kündbar</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {!offerings && isNative() && !offeringsError && (
            <div className="pw-loading">Lade Angebote…</div>
          )}

          {!offerings && offeringsError && (
            <div className="pw-offerings-error">
              <p>Angebote konnten nicht geladen werden.</p>
              <button className="pw-retry" onClick={loadOfferings}>Erneut versuchen</button>
            </div>
          )}

          {error && <p className="pw-error">{error}</p>}
        </main>

        {/* CTA Footer */}
        <footer className="pw-footer">
          <button
            className="pw-cta"
            onClick={isNative() ? handlePurchase : onClose}
            disabled={loading || (isNative() && !selected)}
          >
            {loading ? <span className="pw-spinner" /> : 'Pro freischalten'}
          </button>

          <button className="pw-skip" onClick={onClose}>Nicht jetzt</button>

          {isNative() && (
            <button className="pw-restore" onClick={handleRestore} disabled={restoring}>
              {restoring ? 'Wird wiederhergestellt…' : 'Käufe wiederherstellen'}
            </button>
          )}

          <div className="pw-legal-links">
            <a href="https://bullivo.de/privacy.html" target="_blank" rel="noopener noreferrer">Datenschutz</a>
            <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer">Nutzungsbedingungen</a>
            <a href="https://bullivo.de/imprint.html" target="_blank" rel="noopener noreferrer">Impressum</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
